'use strict'

import {
  CircularProgress,
  Collapse,
  TextField
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { Link } from 'react-router-dom'
import { getCrewColor, getRouteColor } from './crewColors'
import { formatMinutes } from './mapUtils'

/**
 * Desert Flora styled property popover.
 * Float mode: .prop-popover class. Name + close, crew badge, address,
 * chip row (Freq, Est, Act), reassign section with route select + action buttons.
 */
export default function PropertyPopover ({
  selectedProps, selectedAssignedId, selectedEffectiveName,
  selectedFrequencyLabel, selectedMaintenanceDays,
  selectedIsOnRoute, selectedIsRouteUnlinked, focusCrewId, focusDate,
  focusCrewName, routeIds, crewIds,
  selectedScenarioId, routeActionPending,
  unassignProperty, addToDay,
  assignmentOptions = [], assignmentRouteId = '', setAssignmentRouteId,
  moveSelectedProperty, hasAnyMoveChange, pendingChanges = [],
  applyingMove, suggestNearestCrew, suggestingCrew,
  bestFitData, bestFitLoading, bestFitOpen, setBestFitOpen,
  bestFitAssign
}) {
  if (!selectedProps) {
    return (
      <div style={{
        padding: '13px 15px 12px', display: 'flex', flexDirection: 'column', gap: 9,
        fontSize: 12.5, color: 'var(--df-ink-4, #97a094)'
      }}>
        Click a property pin to open details.
      </div>
    )
  }

  const crewColor = selectedAssignedId
    ? (getRouteColor(selectedAssignedId, routeIds) || getCrewColor(selectedAssignedId, crewIds))
    : '#9E9E9E'

  const estMin = Number(selectedProps.estimatedMinutesOnSite || 0) || Math.round((selectedProps.estimatedHours || 0) * 60)
  const actMin = selectedProps.medianOnSiteMinutes || (selectedProps.medianOnSiteHours ? Math.round(selectedProps.medianOnSiteHours * 60) : 0)
  const assignmentAutocompleteOptions = [
    { id: '', name: 'Unassigned' },
    ...assignmentOptions
  ]
  const selectedAssignmentOption = assignmentAutocompleteOptions.find(option => String(option.id || '') === String(assignmentRouteId || '')) || assignmentAutocompleteOptions[0]

  return (
    <>
      <div style={{ padding: '13px 15px 12px', display: 'flex', flexDirection: 'column', gap: 9 }}>
        {/* Name */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink, #1c2419)', lineHeight: 1.25 }}>
              {selectedProps.propertyName || '-'}
            </div>
          </div>
        </div>

        {/* Crew badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="prop-crew-badge" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 99,
            background: 'var(--df-primary-soft, #e3eee0)',
            border: '1px solid #b6d4be', maxWidth: '100%'
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: crewColor
            }} />
            <span style={{
              fontSize: 11.5, fontWeight: 600, color: '#164a2c',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }}>
              {selectedEffectiveName}
            </span>
          </span>
          {selectedIsOnRoute && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 99,
              background: '#dde9f5', color: '#0277bd'
            }}>On route</span>
          )}
          {selectedIsRouteUnlinked && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
              background: 'var(--df-warn-bg, #fff3d9)', color: 'var(--df-warn-text, #8c5b00)'
            }}>Unlinked</span>
          )}
        </div>

        {/* Address */}
        <div style={{ fontSize: 12, color: 'var(--df-ink-3, #6f7a6b)' }}>
          <Link
            style={{ color: 'var(--df-primary, #1f5e3a)', textDecoration: 'underline', cursor: 'pointer' }}
            target="_blank"
            to={{ pathname: `https://cloud.youraspire.com/app/properties/overview/${selectedProps.aspirePropertyId}` }}
          >
            {selectedProps.address || '-'}{selectedProps.city ? `, ${selectedProps.city}` : ''}
          </Link>
          <span style={{ color: 'var(--df-ink-4, #97a094)' }}> · Aspire #{selectedProps.aspirePropertyId}</span>
        </div>

        {/* Chips: Freq, Est, Act, VIP, Locked, Anchor */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className="prop-chip" style={{
            fontSize: 11, fontWeight: 600, color: 'var(--df-ink-2, #3d463a)',
            padding: '3px 9px', border: '1px solid var(--df-line, #e0d8c2)',
            borderRadius: 99, background: 'var(--df-surface-2, #fbf8f1)'
          }}>
            Freq · {selectedFrequencyLabel || selectedProps.frequencyCode || '?'}
          </span>
          <span className="prop-chip" style={{
            fontSize: 11, fontWeight: 600, color: 'var(--df-ink-2, #3d463a)',
            padding: '3px 9px', border: '1px solid var(--df-line, #e0d8c2)',
            borderRadius: 99, background: 'var(--df-surface-2, #fbf8f1)'
          }}>
            Est {formatMinutes(estMin)}
          </span>
          {actMin > 0 && (
            <span className="prop-chip accent" style={{
              fontSize: 11, fontWeight: 700, color: '#164a2c',
              padding: '3px 9px', border: '1px solid #b6d4be',
              borderRadius: 99, background: 'var(--df-primary-soft, #e3eee0)'
            }}>
              Act {formatMinutes(actMin)}
            </span>
          )}
          {selectedProps.isVip && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
              background: '#f8d9a8', color: '#8a4a12'
            }}>VIP</span>
          )}
          {selectedProps.isLocked && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
              background: 'var(--df-bg-2, #ece5d3)', color: 'var(--df-ink-3, #6f7a6b)'
            }}>Locked</span>
          )}
          {selectedProps.isAnchor && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
              background: 'var(--df-bg-2, #ece5d3)', color: 'var(--df-ink-3, #6f7a6b)'
            }}>Anchor</span>
          )}
        </div>

        {/* Assigned days */}
        {selectedMaintenanceDays.length > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)' }}>
            Assigned: <span style={{ color: 'var(--df-ink-2, #3d463a)', fontWeight: 600 }}>
              {selectedMaintenanceDays.join(', ')}
              {selectedProps.weekType ? ` (${selectedProps.weekType})` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Manual assignment */}
      {!!selectedScenarioId && (
        <div className="prop-reassign" style={{
          padding: '12px 15px', borderTop: '1px solid var(--df-line, #ede6d2)',
          background: 'var(--df-surface-2, #faf6ee)'
        }}>
          <div className="reassign-label" style={{
            fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4, #97a094)',
            textTransform: 'uppercase', letterSpacing: 0, marginBottom: 7
          }}>Assignment</div>
          <Autocomplete
            size="small"
            options={assignmentAutocompleteOptions}
            value={selectedAssignmentOption}
            getOptionLabel={(option) => option?.name || ''}
            getOptionSelected={(option, value) => String(option?.id || '') === String(value?.id || '')}
            onChange={(_event, option) => setAssignmentRouteId && setAssignmentRouteId(option?.id || '')}
            disabled={routeActionPending || applyingMove}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Search route or crew"
                InputProps={{
                  ...params.InputProps,
                  style: {
                    ...params.InputProps.style,
                    background: '#fff',
                    borderRadius: 6,
                    fontSize: 12,
                    fontFamily: 'inherit'
                  }
                }}
              />
            )}
          />
          {pendingChanges.length > 0 && (
            <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {pendingChanges.map(change => (
                <span key={change} style={{ fontSize: 11, color: 'var(--df-ink-3, #6f7a6b)' }}>
                  {change}
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              type="button"
              className="prop-action-btn ghost"
              style={{
                padding: '5px 10px', borderRadius: 6, border: '1px solid var(--df-line, #e0d8c2)',
                cursor: 'pointer', fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'transparent', color: 'var(--df-ink-3, #6f7a6b)', transition: 'all 0.12s'
              }}
              onClick={suggestNearestCrew}
              disabled={!suggestNearestCrew || routeActionPending || applyingMove || suggestingCrew}
            >
              {suggestingCrew ? <CircularProgress size={12} style={{ color: 'var(--df-primary, #1f5e3a)' }} /> : 'Nearest'}
            </button>
            <button
              type="button"
              className="prop-action-btn primary"
              style={{
                padding: '5px 10px', borderRadius: 6, border: 'none',
                cursor: hasAnyMoveChange ? 'pointer' : 'not-allowed',
                fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                background: 'var(--df-primary, #1f5e3a)', color: '#fff',
                transition: 'all 0.12s', marginLeft: 'auto',
                opacity: hasAnyMoveChange ? 1 : 0.45
              }}
              onClick={moveSelectedProperty}
              disabled={!moveSelectedProperty || !hasAnyMoveChange || routeActionPending || applyingMove}
            >
              {(routeActionPending || applyingMove)
                ? <CircularProgress size={12} style={{ color: '#fff' }} />
                : 'Apply'
              }
            </button>
          </div>
        </div>
      )}

      {/* Reassign actions */}
      <div style={{
        padding: '12px 15px', borderTop: '1px solid var(--df-line, #ede6d2)',
        background: 'var(--df-surface-2, #faf6ee)'
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4, #97a094)',
          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7
        }}>Actions</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {!!selectedAssignedId && (
            <button
              className="prop-action-btn danger"
              style={{
                padding: '5px 10px', borderRadius: 6, border: '1px solid #e8c0b8',
                cursor: 'pointer', fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: '#fce8e4', color: '#a9341d', transition: 'all 0.12s'
              }}
              onClick={() => unassignProperty(selectedProps.aspirePropertyId)}
              disabled={!selectedScenarioId || routeActionPending}
            >
              {routeActionPending
                ? <CircularProgress size={12} style={{ color: '#a9341d' }} />
                : <LinkOffIcon style={{ fontSize: 14 }} />
              }
              Unassign
            </button>
          )}

          {!!focusCrewId && !!focusDate && !selectedIsOnRoute && (
            <button
              className="prop-action-btn primary"
              style={{
                padding: '5px 10px', borderRadius: 6, border: 'none',
                cursor: 'pointer', fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'var(--df-primary, #1f5e3a)', color: '#fff',
                transition: 'all 0.12s', marginLeft: 'auto'
              }}
              onClick={() => addToDay(selectedProps.aspirePropertyId)}
              disabled={!selectedScenarioId || routeActionPending}
            >
              {routeActionPending
                ? <CircularProgress size={12} style={{ color: '#fff' }} />
                : <AddCircleOutlineIcon style={{ fontSize: 14 }} />
              }
              {selectedAssignedId ? `Move to ${focusCrewName}` : `Assign to ${focusCrewName}`}
            </button>
          )}
        </div>
      </div>

      {/* Best Fit Suggestions (unassigned properties only) */}
      {!!bestFitData && !selectedAssignedId && (
        <div style={{ borderTop: '1px solid var(--df-line, #ede6d2)' }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 15px', cursor: 'pointer', userSelect: 'none'
            }}
            onClick={() => setBestFitOpen(prev => !prev)}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--df-ink, #1c2419)' }}>
              Best Fit{bestFitData.suggestions?.length ? ` (top ${Math.min(bestFitData.suggestions.length, 5)})` : ''}
            </span>
            {bestFitOpen
              ? <ExpandLessIcon style={{ fontSize: 16, color: 'var(--df-ink-4, #97a094)' }} />
              : <ExpandMoreIcon style={{ fontSize: 16, color: 'var(--df-ink-4, #97a094)' }} />
            }
          </div>
          <Collapse in={bestFitOpen}>
            <div style={{ padding: '0 15px 12px' }}>
              {bestFitLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CircularProgress size={14} style={{ color: 'var(--df-primary, #1f5e3a)' }} />
                  <span style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)' }}>Analyzing routes...</span>
                </div>
              )}
              {!bestFitLoading && (!bestFitData.suggestions || bestFitData.suggestions.length === 0) && (
                <span style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)' }}>No suggestions available.</span>
              )}
              {!bestFitLoading && bestFitData.suggestions?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {bestFitData.suggestions.slice(0, 5).map(s => {
                    const capPct = Math.round(s.projectedMetrics?.capacityPct || 0)
                    const barColor = capPct >= 100 ? '#a9341d' : capPct >= 85 ? '#b35d1a' : '#2f7a3e'
                    return (
                      <div
                        key={`${s.routeId}-${s.dayOfWeek}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 8px', borderRadius: 8,
                          border: '1px solid var(--df-line, #e0d8c2)',
                          background: 'var(--df-surface, #fbf8f1)'
                        }}
                      >
                        <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--df-ink-4, #97a094)', minWidth: 16, textAlign: 'center', flexShrink: 0 }}>
                          {s.rank}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--df-ink, #1c2419)', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.routeName} — {s.dayOfWeek}
                          </div>
                          <div style={{ fontSize: 10.5, color: 'var(--df-ink-4, #97a094)', lineHeight: 1.3 }}>
                            {s.distanceMiles != null ? `${s.distanceMiles} mi` : '?'} · {formatMinutes(s.dayMetrics?.totalMin || 0)} / 8h · {s.dayMetrics?.stops || 0} stops
                          </div>
                          <div style={{ height: 4, borderRadius: 99, background: 'var(--df-bg-2, #ece5d3)', overflow: 'hidden', marginTop: 3 }}>
                            <div style={{ height: '100%', width: `${Math.min(100, capPct)}%`, background: barColor, borderRadius: 99 }} />
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--df-ink-4, #97a094)', marginTop: 1 }}>
                            Projected: {formatMinutes(s.projectedMetrics?.totalMin || 0)} ({capPct}%)
                            {s.projectedMetrics?.otMin > 0 && <span style={{ color: '#a9341d', fontWeight: 600 }}> +{formatMinutes(s.projectedMetrics.otMin)} OT</span>}
                          </div>
                        </div>
                        <button
                          style={{
                            padding: '4px 8px', borderRadius: 5, border: 'none',
                            cursor: 'pointer', fontSize: 10.5, fontWeight: 700, fontFamily: 'inherit',
                            background: 'var(--df-primary, #1f5e3a)', color: '#fff',
                            flexShrink: 0, transition: 'all 0.12s'
                          }}
                          onClick={() => bestFitAssign(s)}
                          disabled={!selectedScenarioId || routeActionPending}
                        >
                          {routeActionPending ? <CircularProgress size={12} style={{ color: '#fff' }} /> : 'ASSIGN'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </Collapse>
        </div>
      )}
      {bestFitLoading && !bestFitData && !selectedAssignedId && (
        <div style={{
          padding: '10px 15px', borderTop: '1px solid var(--df-line, #ede6d2)',
          display: 'flex', alignItems: 'center', gap: 8
        }}>
          <CircularProgress size={14} style={{ color: 'var(--df-primary, #1f5e3a)' }} />
          <span style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)' }}>Finding best fit...</span>
        </div>
      )}
    </>
  )
}
